// Section 24.8 and 25 (M5d): projection is the steward's planning tool. When something essential
// is at risk (a shortfall in the planning model, a shortage last season, or many weak workers),
// a small set of candidate plans is projected four seasons with project() and the one with the
// lowest projected fragility (plus penalties for expected deaths and people short) is chosen.
// The number of candidates is capped by a person-season budget so a decision for 200 people
// stays inside the Section 2 budget. Ties keep the earlier (default) candidate.
import { lastCompletedReport } from '../../core/queries';
import type { Decision, Projection } from '../../core/types';
import { project } from '../project';
import { hasWeakFree, planCrew, weakShare, type BuildNeeds, type Plan, type Variant } from './crew';
import type { StewardCtx } from './model';

export function projectionScore(
  sc: StewardCtx,
  projection: Projection,
  horizon = sc.horizon,
): number {
  const w = sc.cfg.steward;
  const t = sc.cfg.needs.shortThreshold;
  let healthNow = 0;
  for (const p of sc.people) healthNow += p.health;
  healthNow = sc.pop === 0 ? 0 : healthNow / sc.pop;
  let total = 0;
  for (const season of projection.seasons) {
    const needs = Object.values(season.needs);
    const n = Math.max(1, needs.length);
    let water = 0;
    let food = 0;
    let warmth = 0;
    for (const x of needs) {
      if (x.water < t) water++;
      if (x.food < t) food++;
      if (x.warmth < t) warmth++;
    }
    let healthLost = 0;
    if (season.health !== undefined) {
      const values = Object.values(season.health);
      let mean = 0;
      for (const h of values) mean += h;
      mean = values.length === 0 ? 0 : mean / values.length;
      healthLost = Math.max(0, healthNow - mean);
    }
    total +=
      season.fragility +
      w.scoreDeathWeight * season.expectedDeaths +
      (w.scoreWaterShortWeight * water +
        w.scoreFoodShortWeight * food +
        w.scoreWarmthShortWeight * warmth) /
        n +
      w.scoreHealthWeight * healthLost;
  }
  // A projection cut short by a loss scores as the worst season for the rest of the horizon.
  const missing = horizon - projection.seasons.length;
  total += missing * (100 + w.scoreDeathWeight * sc.pop);
  return total / horizon;
}

function sameJobs(a: Plan, b: Plan): boolean {
  if (a.jobs.size !== b.jobs.size) return false;
  for (const [id, job] of a.jobs) if (b.jobs.get(id) !== job) return false;
  return true;
}

/** Something essential is at risk: worth comparing candidates by projection. */
export function atRisk(sc: StewardCtx, base: Plan): boolean {
  if (base.risk) return true;
  const report = lastCompletedReport(sc.state);
  if (
    report?.entries.some(
      (e) =>
        e.kind === 'shortage' &&
        (e.resource === 'food' || e.resource === 'water' || e.resource === 'warmth'),
    ) === true
  )
    return true;
  return weakShare(sc) >= sc.cfg.steward.weakShareTrigger;
}

/**
 * Candidates and horizon the projection budget allows for this settlement's size: as many
 * candidates as fit at the full horizon, else fewer, else a shorter horizon (never below
 * `minProjectedSeasons`). Zero candidates means plan by the heuristic alone.
 */
export function projectionPlan(
  sc: StewardCtx,
  maxCandidates: number,
): { candidates: number; horizon: number } {
  const w = sc.cfg.steward;
  const pop = Math.max(1, sc.pop);
  for (let n = Math.min(w.maxCandidates, maxCandidates); n >= 2; n--) {
    const horizon = Math.min(sc.horizon, Math.floor(w.projectionBudget / (pop * n)));
    if (horizon >= w.minProjectedSeasons) return { candidates: n, horizon };
  }
  return { candidates: 0, horizon: 0 };
}

export interface Choice {
  plan: Plan;
  /** Candidates projected (0 when the default plan was taken without projecting). */
  projected: number;
  scores: { variant: Variant; score: number }[];
}

export function choosePlan(
  sc: StewardCtx,
  builds: BuildNeeds,
  toDecision: (plan: Plan) => Decision,
  maxCandidates?: number,
): Choice {
  const base = planCrew(sc, 'balanced', builds);
  const budget = projectionPlan(sc, maxCandidates ?? Number.MAX_SAFE_INTEGER);
  if (budget.candidates < 2 || !atRisk(sc, base)) return { plan: base, projected: 0, scores: [] };
  const variants: Variant[] = ['invest', 'essentials', 'recover'];
  const candidates: Plan[] = [base];
  for (const v of variants) {
    if (candidates.length >= budget.candidates) break;
    if (v === 'recover' && !hasWeakFree(sc)) continue;
    const plan = planCrew(sc, v, builds);
    if (!candidates.some((c) => sameJobs(c, plan))) candidates.push(plan);
  }
  if (candidates.length < 2) return { plan: base, projected: 0, scores: [] };
  let best = base;
  let bestScore = Number.POSITIVE_INFINITY;
  const scores: Choice['scores'] = [];
  for (const plan of candidates) {
    const projection = project(sc.state, toDecision(plan), budget.horizon, { cfg: sc.cfg });
    // M7: the balanced plan builds, makes tools and keeps guards, which pay off beyond the
    // horizon; another plan must beat it by a margin, or the settlement never invests.
    const margin = plan === base ? 0 : sc.cfg.steward.switchMargin;
    const score = projectionScore(sc, projection, budget.horizon);
    scores.push({ variant: plan.variant, score });
    if (score + margin < bestScore - 1e-9) {
      best = plan;
      bestScore = score + margin;
    }
  }
  return { plan: best, projected: candidates.length, scores };
}
